import { el } from "../lib/dom.js";
import { feedbackLine } from "./feedback.js";
import { MOVE_UNDO } from "./moveUndo.js";
import { SEPARATOR, isEntry, type MenuItem } from "./menu.js";
import type { ScreenHost } from "./ui.js";

/**
 * Reorder items and Reorder lists: the phone's explicit way to move rows (g53).
 *
 * Below the 768px rail breakpoint a row reads as checkbox, content and menu,
 * because a permanent 44px drag column cost the title a third of its width at
 * 390px (the review's F01). The handles come back only while this mode is on,
 * with a sticky bar saying so and a Done that ends it. From 768 up nothing here
 * applies: the handles stay where they have always been and the menus offer no
 * entry.
 *
 * The mode moves nothing itself. Every drag, arrow key and Move up/down still
 * goes through the screen's one `commitMove`, and each one is saved as it
 * lands, so Done finishes the mode and commits nothing.
 *
 * The bar is also where the mode's routine feedback shows (g102): "Moved."
 * takes the label's place for a few seconds, since the bar is the one part of
 * the screen that stays put while the rows scroll. Undo move sits beside Done
 * while there is a drop to undo and it fits at full size without making the
 * bar taller: on the bar's one row from 360px, and on its second row where the
 * label has already wrapped at 200% text. Where it does not, at 320px and
 * 100% text, it stays in the screen's menu, which is where it is after Done
 * as well.
 *
 * One rule for leaving, and the mode never comes back on its own: Done,
 * Escape, anything that opens an editor or an add or edit surface, crossing to
 * 768 or wider, and leaving the screen. A drag in flight is cancelled first, so
 * nothing drops from a pointer that the page has moved on from.
 */
export interface ReorderMode {
  /** The sticky bar. The screen puts it between its header and its rows. */
  bar: HTMLElement;
  /** The menu entry and its separator, or nothing from 768 up. */
  entry(): MenuItem[];
  /** True while the mode is on. */
  active(): boolean;
  /**
   * The control in `row` a focus fallback should land on: its handle while
   * the handles are showing, else its ellipsis. A hidden handle is not a
   * place focus can stay.
   */
  control(row: Element | null | undefined): HTMLElement | undefined;
  /** Wraps an action that opens a surface, so it ends the mode first. */
  leaving<A extends unknown[]>(action: (...args: A) => void): (...args: A) => void;
  /** The same, for every entry of a menu. */
  leavingAll(items: MenuItem[]): MenuItem[];
  /** Ends the mode because a surface is opening. */
  exit(): void;
  /** Shows routine feedback in the bar, in place of its label, for a few seconds. */
  say(message: string): void;
  /** Whether there is a drop to undo, which the bar offers when it has the room. */
  setUndo(available: boolean): void;
  dispose(): void;
}

export function reorderMode(options: {
  shell: ScreenHost;
  /** The rows container. It carries `data-handles`, which the stylesheet reads. */
  rows: HTMLElement;
  noun: "items" | "lists";
  /** The menu button the entry lives behind, where Done returns the focus. */
  opener: HTMLElement;
  reordering: { cancel(): void; active(): boolean };
  /** Resolves any open inline draft before the mode starts. False refuses. */
  prepare?(): boolean;
  /** Re-reads an open menu, whose entry comes and goes with the width. */
  refreshMenu(): void;
  /** Undoes the screen's latest drop. */
  undo(): void;
  /** The mode has started: the screen clears the feedback it showed outside the bar. */
  entered?(): void;
}): ReorderMode {
  const { shell, rows, noun, opener, reordering } = options;
  const labelId = `reorder-bar-${noun}`;
  const done = el("button", {
    class: "reorder-done",
    text: "Done",
    attrs: { type: "button", "aria-describedby": labelId },
    on: { click: () => leave("done") },
  });
  // The feedback is drawn over the label, which keeps its room and stays the
  // group's name: the bar is the same size whatever it is saying.
  const label = el("p", { class: "reorder-bar-label", text: `Reordering ${noun}`, attrs: { id: labelId } });
  const undo = el("button", {
    class: "reorder-undo secondary",
    // Short on the bar, where the room decides whether it is offered at all;
    // its name is the menu entry's.
    text: MOVE_UNDO.short,
    attrs: { type: "button", hidden: true, "aria-label": MOVE_UNDO.label },
    on: { click: () => options.undo() },
  });
  const feedback = feedbackLine({
    class: "reorder-bar-feedback",
    around: change => keepAnchor(change),
    changed: () => fit(),
  });
  const bar = el("div", {
    class: "reorder-bar",
    attrs: { role: "group", "aria-labelledby": labelId, hidden: true },
    children: [
      el("div", { class: "reorder-bar-words", children: [label, feedback.node] }),
      el("div", { class: "reorder-bar-actions", children: [undo, done] }),
    ],
  });

  let on = false;
  let undoable = false;
  let narrow = shell.narrow();
  const root = document.documentElement;

  // The bar's height at 200% text is not the first paint's estimate, and focus
  // moved by the arrow keys has to scroll clear of it: `scroll-padding-top`
  // reads this. Written only while the bar is up.
  const measure = () => root.style.setProperty("--reorder-bar-height", `${bar.getBoundingClientRect().height}px`);
  let observer: ResizeObserver | undefined;

  /**
   * Offers Undo in the bar only where it costs the bar nothing: the same one
   * row, no taller, nothing pushed past the edge. Measured rather than asked
   * of a width, because text size decides it as much as the screen does.
   */
  function fit(): void {
    if (!on || !undoable) { undo.hidden = true; return; }
    undo.hidden = true;
    const without = bar.getBoundingClientRect().height;
    undo.hidden = false;
    if (bar.getBoundingClientRect().height > without + 0.5 || bar.scrollWidth > bar.clientWidth) undo.hidden = true;
  }
  const settle = () => { fit(); measure(); };

  const drawn = (): HTMLElement[] =>
    [...rows.children].filter((row): row is HTMLElement => row instanceof HTMLElement && row.dataset["id"] !== undefined);
  const handles = (): HTMLElement[] =>
    drawn().flatMap(row => row.querySelector<HTMLElement>(":scope > .item-handle") ?? []);

  function paint(): void {
    rows.dataset["handles"] = narrow && !on ? "hidden" : "shown";
    bar.hidden = !on;
  }

  /**
   * Keeps the row being read where it was on screen while the layout changes
   * under it: the bar appearing or going, the handles narrowing every row. The
   * row holding the focus when there is one on screen, else the first row
   * below the bar.
   *
   * One correction, and only this one: Chrome's own scroll anchoring would
   * make a second adjustment for the same shift, so it is switched off for the
   * change, and Safari has none to switch off. Then, in the mode, a row left
   * under the stuck bar is brought out from under it.
   */
  function keepAnchor(change: () => void): void {
    const top = on ? bar.getBoundingClientRect().bottom : 0;
    const focused = document.activeElement?.closest<HTMLElement>("[data-id]");
    const inView = (row: HTMLElement) => {
      const box = row.getBoundingClientRect();
      return box.bottom > top && box.top < window.innerHeight;
    };
    const anchor = focused && rows.contains(focused) && inView(focused)
      ? focused
      : drawn().find(row => row.getBoundingClientRect().bottom > top);
    const before = anchor?.getBoundingClientRect().top;
    root.style.setProperty("overflow-anchor", "none");
    try {
      change();
      if (!anchor || before === undefined || !anchor.isConnected) return;
      const drift = anchor.getBoundingClientRect().top - before;
      if (drift !== 0) window.scrollBy(0, drift);
      if (on) {
        const under = bar.getBoundingClientRect().bottom - anchor.getBoundingClientRect().top;
        if (under > 0) window.scrollBy(0, -under);
      }
    } finally {
      root.style.removeProperty("overflow-anchor");
    }
  }

  /** The first handle below the bar, so entering scrolls nothing. */
  function focusFirst(): void {
    const below = bar.getBoundingClientRect().bottom;
    const all = handles();
    const target = all.find(handle => {
      const box = handle.getBoundingClientRect();
      return box.top >= below && box.bottom <= window.innerHeight;
    }) ?? all[0];
    target?.focus({ preventScroll: true });
  }

  /** A handle that has just been hidden hands the focus to its row's ellipsis. */
  function rescueFocus(): void {
    const focused = document.activeElement;
    if (!(focused instanceof HTMLElement) || !focused.matches(".item-handle") || !rows.contains(focused)) return;
    if (rows.dataset["handles"] !== "hidden") return;
    focused.closest("[data-id]")?.querySelector<HTMLElement>(".item-actions")?.focus({ preventScroll: true });
  }

  function enter(): void {
    if (!narrow) return;
    if (on) {
      focusFirst();
      return;
    }
    if (drawn().length < 2) return;
    // An open row draft is resolved the way switching editors resolves it
    // (E04): saved when valid, refused when blank, and a refusal keeps the
    // person in the field rather than starting the mode over it.
    if (options.prepare && !options.prepare()) return;
    options.entered?.();
    keepAnchor(() => {
      on = true;
      paint();
    });
    settle();
    if (typeof ResizeObserver === "function") {
      observer = new ResizeObserver(settle);
      observer.observe(bar);
    }
    document.addEventListener("keydown", onKey);
    shell.announce(`Reordering ${noun}. Each move is saved as you make it.`);
    focusFirst();
  }

  function leave(reason: "done" | "surface" | "wide"): void {
    if (!on) return;
    const focused = document.activeElement;
    // First, so a drag in flight ends against the rows it started on.
    reordering.cancel();
    feedback.clear();
    keepAnchor(() => {
      on = false;
      paint();
    });
    fit();
    observer?.disconnect();
    observer = undefined;
    root.style.removeProperty("--reorder-bar-height");
    document.removeEventListener("keydown", onKey);
    if (reason === "done") {
      shell.announce(`Finished reordering ${noun}.`);
      opener.focus({ preventScroll: true });
    } else if (reason === "wide" && focused === done) {
      opener.focus({ preventScroll: true });
    }
    rescueFocus();
  }

  const onKey = (event: KeyboardEvent) => {
    // The drag controller's own Escape runs first and marks the event: that
    // Escape cancelled the drag and is spent. The next one ends the mode.
    if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) return;
    const target = event.target;
    if (target instanceof Element && target.closest("dialog, [popover], textarea, input")) return;
    if (reordering.active()) return;
    event.preventDefault();
    leave("done");
  };

  const stopNarrow = shell.onNarrow(next => {
    narrow = next;
    if (!next) {
      if (on) leave("wide");
      else paint();
    } else {
      // The rail breakpoint crossed mid-drag: the handle under the pointer is
      // about to disappear, so the gesture ends here, unwritten.
      reordering.cancel();
      paint();
      rescueFocus();
    }
    options.refreshMenu();
  });

  paint();

  const leaving = <A extends unknown[]>(action: (...args: A) => void) => (...args: A) => {
    leave("surface");
    action(...args);
  };

  return {
    bar,
    entry: () => narrow
      ? [{ label: `Reorder ${noun}`, icon: "grip", disabled: drawn().length < 2, onSelect: enter }, SEPARATOR]
      : [],
    active: () => on,
    control: row => row?.querySelector<HTMLElement>(rows.dataset["handles"] === "hidden" ? ".item-actions" : ".item-handle") ?? undefined,
    leaving,
    leavingAll: items => items.map(entry => (isEntry(entry) ? { ...entry, onSelect: leaving(entry.onSelect) } : entry)),
    exit: () => leave("surface"),
    say: message => { if (on) feedback.show(message); },
    setUndo(available) {
      const held = document.activeElement === undo;
      undoable = available;
      fit();
      // The button goes with the drop it undid, and the focus moves to Done.
      if (held && undo.hidden && on) done.focus({ preventScroll: true });
    },
    dispose() {
      feedback.dispose();
      stopNarrow();
      document.removeEventListener("keydown", onKey);
      observer?.disconnect();
      observer = undefined;
      root.style.removeProperty("--reorder-bar-height");
      on = false;
    },
  };
}
